import React, { useState, useEffect } from 'react';
import './PaymentModal.css';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalAmount: number;
    onConfirmPayment: (amount: number, method: 'CASH' | 'POS' | 'CUSTOM') => void;
    title?: string;
    currency?: string;
    initialMethod?: 'CASH' | 'POS' | 'CUSTOM'; // 🆕 Metodo iniziale
}

const QUICK_AMOUNTS = [
    0.10, 0.20, 0.50, 1.00, 2.00,
    5.00, 10.00, 20.00, 50.00, 100.00,
    0.01, 0.02, 0.05, 200.00, 500.00
];

export const PaymentModal: React.FC<PaymentModalProps> = ({
    isOpen,
    onClose,
    totalAmount,
    onConfirmPayment,
    title = 'Tipo di pagamento',
    currency = '€',
    initialMethod = 'CASH' // 🆕 Default CASH se non specificato
}) => {
    const [paidAmount, setPaidAmount] = useState<number>(0);
    const [selectedMethod, setSelectedMethod] = useState<'CASH' | 'POS' | 'CUSTOM'>('CASH');

    useEffect(() => {
        if (isOpen) {
            setPaidAmount(0);
            setSelectedMethod(initialMethod); // 🔧 Usa il metodo iniziale passato come prop
        }
    }, [isOpen, initialMethod]);

    if (!isOpen) return null;

    const remaining = Math.max(0, totalAmount - paidAmount);
    const change = Math.max(0, paidAmount - totalAmount);

    const handleQuickAmount = (amount: number) => {
        setPaidAmount(prev => Number((prev + amount).toFixed(2)));
    };

    const handleConfirm = () => {
        if (paidAmount >= totalAmount) {
            onConfirmPayment(paidAmount, selectedMethod);
            onClose();
        }
    };

    const handleReset = () => {
        setPaidAmount(0);
    };

    const formatCurrency = (value: number) => {
        return `${currency} ${value.toFixed(2)}`;
    };

    return (
        <div className="payment-modal-overlay" onClick={onClose}>
            <div className="payment-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="payment-modal-header">
                    <button className="payment-modal-back" onClick={onClose}>
                        ← {title}
                    </button>
                </div>

                {/* Payment Method Tabs */}
                <div className="payment-modal-methods">
                    <button
                        className={`payment-method-btn ${selectedMethod === 'CASH' ? 'active' : ''}`}
                        onClick={() => setSelectedMethod('CASH')}
                    >
                        💵 Contante
                    </button>
                    <button
                        className={`payment-method-btn ${selectedMethod === 'POS' ? 'active' : ''}`}
                        onClick={() => setSelectedMethod('POS')}
                    >
                        💳 POS/Carta
                    </button>
                    <button
                        className={`payment-method-btn ${selectedMethod === 'CUSTOM' ? 'active' : ''}`}
                        onClick={() => setSelectedMethod('CUSTOM')}
                    >
                        🎫 Altro
                    </button>
                </div>

                {/* Summary Cards */}
                <div className="payment-modal-summary">
                    <div className="payment-summary-card">
                        <div className="payment-summary-label">TOTALE</div>
                        <div className="payment-summary-value total">{formatCurrency(totalAmount)}</div>
                    </div>
                    <div className="payment-summary-card">
                        <div className="payment-summary-label">PAGATO</div>
                        <div className="payment-summary-value paid">{formatCurrency(paidAmount)}</div>
                    </div>
                    <div className="payment-summary-card">
                        <div className="payment-summary-label">DA PAGARE</div>
                        <div className="payment-summary-value remaining">{formatCurrency(remaining)}</div>
                    </div>
                </div>

                {/* Quick Amount Buttons */}
                <div className="payment-modal-content">
                    <div className="payment-quick-amounts">
                        {QUICK_AMOUNTS.map((amount) => (
                            <button
                                key={amount}
                                className="payment-quick-btn"
                                onClick={() => handleQuickAmount(amount)}
                            >
                                {formatCurrency(amount)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Change Display */}
                {change > 0 && (
                    <div className="payment-change-display">
                        <span className="payment-change-label">Resto:</span>
                        <span className="payment-change-value">{formatCurrency(change)}</span>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="payment-modal-actions">
                    <button
                        className="payment-action-btn payment-action-reset"
                        onClick={handleReset}
                    >
                        🔄 Reset
                    </button>
                    <button
                        className="payment-action-btn payment-action-confirm"
                        onClick={handleConfirm}
                        disabled={paidAmount < totalAmount}
                    >
                        ✓ Conferma Pagamento
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
